/* Connector-backed renderers for the Wikipedia workroom.
 *
 * These consume the live Wikimedia facts record (api/wikipedia.ts) and render
 * article health, the citation register, the edits & actors timeline, and the
 * read-only article content. Honesty is load-bearing:
 *   • Every panel degrades by connector status (loading / unavailable) with a
 *     truthful message — never a fabricated number.
 *   • A subresource the connector marks `partial`/`unavailable` renders as such;
 *     the pageviews window states its REAL observed span, never the requested 90.
 *   • Reliability classification, dead-link checks, and ORES/Lift Wing scores
 *     are not wired — citations show their real domain + reference count with
 *     "not classified"/"not checked", and edits carry behavioral signals only
 *     (no identity or motive is inferred).
 * Lit conventions per views/wiki.ts. Tokens only (wikiWorkroom.css).
 */
import { html, nothing, type TemplateResult } from 'lit';
import type { WpArticle, WpArticleCoverage, WpRecord } from '../../api/wikipedia.ts';
import type { ConnectorState } from './wikiConnector.ts';

/* ── shared helpers ────────────────────────────────────────────────────────── */
function primaryArticle(record: WpRecord): WpArticle | null {
  return record.articles[0] ?? null;
}
function primaryCoverage(record: WpRecord): WpArticleCoverage | null {
  return record.coverage.articles[0] ?? null;
}
function stamp(ts: string | null | undefined): string {
  if (!ts) return '—';
  return ts.length >= 16 ? `${ts.slice(0, 10)} · ${ts.slice(11, 16)}Z` : ts.slice(0, 10);
}
function protectionLabel(protection: WpArticle['protection']): string {
  if (protection == null) return 'unknown';
  if (!protection.length) return 'None';
  const edit = protection.find((p) => p.type === 'edit') ?? protection[0];
  const level = edit?.level || '';
  if (/sysop/.test(level)) return 'Fully protected';
  if (/(extendedconfirmed|autoconfirmed)/.test(level)) return 'Semi-protected';
  return level || 'Protected';
}
function isRevert(comment: string): boolean {
  return /\b(revert|undid|rvv|rollback)/i.test(comment || '');
}

/** A neutral loading / honest-unavailable block shared by all connector panels.
 * Returns `null` when the connector has data (`ok`) so the caller renders real
 * content instead. */
function statusBlock(conn: ConnectorState, subject: string): TemplateResult | null {
  if (conn.status === 'ok' && conn.record) return null;
  if (conn.status === 'loading') {
    return html`<div class="ww-conn-note">Checking Wikimedia for ${subject}…</div>`;
  }
  if (conn.status === 'idle' || !conn.article) {
    return html`<div class="ww-connector-absent">
      <b>No Wikipedia article mapped for this client.</b> ${subject} appears once a
      <code>*.wikipedia.org</code> asset is mapped in this client's research. Nothing is fabricated here.
    </div>`;
  }
  return html`<div class="ww-connector-absent">
    <b>Wikimedia returned no article facts.</b> ${conn.reason || 'The fetch was unavailable.'} ${subject}
    stays unavailable — the connector never substitutes an inferred value for a live one.
  </div>`;
}

/* ── measured article health (connector block for the health-l column) ─────── */
export function connectorHealthBlock(conn: ConnectorState): TemplateResult {
  const blocked = statusBlock(conn, 'Article health — quality, protection, revision velocity and pageviews —');
  if (blocked) return blocked;
  const record = conn.record as WpRecord;
  const article = primaryArticle(record);
  const coverage = primaryCoverage(record);
  if (!article || !coverage) return html`<div class="ww-conn-note">No primary article in the connector record.</div>`;

  const pv = coverage.subresources.pageviews;
  const series = article.pageviews?.series || [];
  const totalViews = series.reduce((sum, d) => sum + d.views, 0);
  const peak = series.reduce<{ date: string; views: number } | null>(
    (best, d) => (best && best.views >= d.views ? best : d),
    null,
  );
  const revStatus = coverage.subresources.revisions.status;

  return html`<div class="ww-conn">
    <a class="ww-article-link" href=${article.canonicalUrl} target="_blank" rel="noopener"
      >${article.title} ↗</a
    >
    <div class="ww-realfacts">
      <div class="ww-stat"><span>Quality class</span><strong>${article.qualityClass || '—'}</strong>
        <small>${article.qualityClass ? 'WikiProject assessment' : 'Lift Wing not wired'}</small></div>
      <div class="ww-stat"><span>Protection</span><strong>${protectionLabel(article.protection)}</strong>
        <small>page info · live</small></div>
      <div class="ww-stat"><span>Last edit</span><strong>${stamp(article.revisionTs)}</strong>
        <small>${article.revisionId ? `rev ${article.revisionId}` : 'revision unknown'}${
          revStatus === 'partial' || revStatus === 'unavailable' ? ' · history partial' : ''
        }</small></div>
      <div class="ww-stat"><span>Pageviews window</span><strong>${pv.daysFetched}d</strong>
        <small>of ${pv.requestedDays}d requested${pv.daysMissing ? ` · ${pv.daysMissing} not yet published` : ''}</small></div>
    </div>
    ${series.length ? html`<div class="ww-sparkwrap">
      ${sparkline(series)}
      <div class="ww-sparknote">
        <span><b>${totalViews.toLocaleString()}</b> views over the observed span${
          peak ? html` · peak <b>${peak.views.toLocaleString()}</b> on ${peak.date}` : nothing
        }</span>
        ${pv.status === 'partial'
          ? html`<span class="ww-partial">Window is partial — Wikimedia has not yet published ${pv.daysMissing} day(s). The span shown is real, not padded to ${pv.requestedDays}.</span>`
          : nothing}
      </div>
    </div>` : html`<div class="ww-conn-note">Daily pageviews are unavailable for this article this fetch.</div>`}
    <div class="ww-source">MEASURED (Wikimedia Action + Pageviews API) · fetched ${record.fetchedAt.slice(0, 16).replace('T', ' ')}Z · no landed-edit marker shown (no verified edit event on record).</div>
  </div>`;
}

/** Compact real pageview sparkline. Geometry is unitless (adherence-safe);
 * colour comes from the .ww-spark* token classes. */
function sparkline(series: Array<{ date: string; views: number }>): TemplateResult {
  const w = 100;
  const h = 26;
  const max = Math.max(...series.map((d) => d.views), 1);
  const n = series.length;
  const x = (i: number): number => (n <= 1 ? 0 : (i / (n - 1)) * w);
  const y = (v: number): number => h - (v / max) * (h - 2) - 1;
  const line = series.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(d.views).toFixed(1)}`).join(' ');
  const area = `${line} L${w} ${h} L0 ${h} Z`;
  return html`<svg class="ww-spark" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" role="img"
    aria-label="daily pageviews over the observed window">
    <path class="ww-spark-area" d=${area}></path>
    <path class="ww-spark-line" d=${line}></path>
  </svg>`;
}

/* ── panel: Citations register (real links, honest classification) ─────────── */
export function connectorCitationsPanel(conn: ConnectorState): TemplateResult {
  const blocked = statusBlock(conn, 'The citation register');
  if (blocked) return html`<section class="ww-panel"><div class="ww-card">
    <span class="ww-eyebrow">Citation register</span>
    <h2 class="ww-h2">Sources shaping the narrative</h2>${blocked}</div></section>`;

  const record = conn.record as WpRecord;
  const article = primaryArticle(record);
  const coverage = primaryCoverage(record);
  const sub = coverage?.subresources.citations;
  if (!article || article.citations == null) {
    return html`<section class="ww-panel"><div class="ww-card">
      <span class="ww-eyebrow">Citation register</span>
      <h2 class="ww-h2">Citations unavailable this fetch</h2>
      <p class="ww-empty">The article's external-link set could not be read from Wikimedia
        (${sub?.error?.code || 'unavailable'}). No sources are fabricated — the register fills in when the fetch succeeds.</p>
    </div></section>`;
  }

  const citations = article.citations;
  // Domain rollup: one row per host, summing reference counts — the honest
  // shape given reliability/role is not yet classified.
  const byDomain = new Map<string, number>();
  for (const c of citations) {
    const key = c.domain || hostOf(c.url) || '(unknown host)';
    byDomain.set(key, (byDomain.get(key) || 0) + c.refCount);
  }
  const rows = [...byDomain.entries()].sort((a, b) => b[1] - a[1]);
  const CAP = 40;
  const shown = rows.slice(0, CAP);
  const totalRefs = citations.reduce((s, c) => s + c.refCount, 0);

  return html`<section class="ww-panel"><div class="ww-stack">
    <div class="ww-card">
      <div class="ww-card-head">
        <div><span class="ww-eyebrow">Citation register · external links</span>
          <h2 class="ww-h2">${citations.length} sources across ${byDomain.size} domains</h2></div>
        <span class="ww-source">Action API extlinks${sub?.truncated ? ' · truncated at ceiling' : ''}</span>
      </div>
      <p class="ww-note hub-muted">Every row is a real cited domain and its reference count. Reliability
        classification (WP:RS / perennial sources) and dead-link checks are <b>not wired</b> — no reliability
        verdict or dead/alive state is invented; each shows "not classified" / "not checked".</p>
      <div class="ww-tablewrap"><table class="ww-table">
        <thead><tr><th>Source domain</th><th>References</th><th>Reliability / role</th><th>Link state</th></tr></thead>
        <tbody>${shown.map(([domain, refs]) => html`<tr>
          <td><b>${domain}</b></td>
          <td class="ww-mono">${refs}×</td>
          <td class="ww-blocked">not classified</td>
          <td class="ww-blocked">not checked</td>
        </tr>`)}</tbody>
      </table></div>
      ${rows.length > CAP
        ? html`<p class="ww-note hub-muted">+${rows.length - CAP} more domains (coverage: ${totalRefs} references across ${byDomain.size} domains total — nothing dropped silently).</p>`
        : nothing}
    </div>
  </div></section>`;
}

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
}

/* ── panel: Edits & Actors (real revisions, behavioral signals only) ───────── */
export function connectorEditsPanel(conn: ConnectorState): TemplateResult {
  const blocked = statusBlock(conn, 'The revision timeline');
  if (blocked) return html`<section class="ww-panel"><div class="ww-card">
    <span class="ww-eyebrow">Revision timeline & actors</span>
    <h2 class="ww-h2">Edit history</h2>${blocked}</div></section>`;

  const record = conn.record as WpRecord;
  const article = primaryArticle(record);
  const coverage = primaryCoverage(record);
  const editors = article?.editors;
  const revSub = coverage?.subresources.revisions;
  if (!article || !editors) return html`<section class="ww-panel"><div class="ww-card">
    <p class="ww-empty">No revision data in the connector record.</p></div></section>`;

  const recent = editors.recent;
  return html`<section class="ww-panel"><div class="ww-stack">
    <div class="ww-card">
      <div class="ww-card-head">
        <div><span class="ww-eyebrow">Editor composition · fetched window</span>
          <h2 class="ww-h2">Who is editing</h2></div>
        <span class="ww-source">Action API revisions + contributors</span>
      </div>
      <div class="ww-realfacts">
        <div class="ww-stat"><span>Registered contributors</span><strong>${editors.registered}</strong>
          <small>named accounts (capped at ceiling)</small></div>
        <div class="ww-stat"><span>Anon in window</span><strong>${editors.anon}</strong>
          <small>IP editors among fetched revisions</small></div>
        <div class="ww-stat"><span>Revert-like edits</span><strong>${editors.reverts}</strong>
          <small>comment matched revert/undo</small></div>
      </div>
      <p class="ww-note hub-muted">Behavioral signals only — <b>no identity or motive is inferred</b> from any edit.
        ORES / Lift Wing damaging scores and IP geolocation are not wired; none are shown.</p>
    </div>
    <div class="ww-card">
      <div class="ww-card-head">
        <div><span class="ww-eyebrow">Revision timeline · ${recent.length} most recent</span>
          <h2 class="ww-h2">Edit velocity</h2></div>
        ${revSub && (revSub.status === 'partial' || revSub.status === 'unavailable')
          ? html`<span class="ww-chip amber">history ${revSub.status}${revSub.error?.code ? ` · ${revSub.error.code}` : ''}</span>`
          : nothing}
      </div>
      ${recent.length
        ? html`<div class="ww-timeline">${recent.map((r) => html`<div class=${`ww-event ${isRevert(r.comment) ? 'risk' : ''}`}>
            <div class="ww-event-head">
              <b>${stamp(r.timestamp)}</b>
              <span class=${`ww-chip ${r.registered ? '' : 'amber'}`}>${r.registered ? 'registered' : 'anonymous IP'}</span>
              ${isRevert(r.comment) ? html`<span class="ww-chip neg">revert-like</span>` : nothing}
            </div>
            <p class="ww-event-comment">${r.comment || '(no edit summary)'}</p>
            <small class="ww-mono">${r.user || 'unknown'} · rev ${r.revisionId ?? '—'}</small>
          </div>`)}</div>`
        : html`<p class="ww-empty">No revisions returned in the fetched window.</p>`}
    </div>
  </div></section>`;
}

/* ── panel: Content (real read-only article, clearly unannotated) ──────────── */
export function connectorContentPanel(conn: ConnectorState): TemplateResult {
  const blocked = statusBlock(conn, 'The article workspace');
  if (blocked) return html`<section class="ww-panel"><div class="ww-card">
    <span class="ww-eyebrow">Annotated article</span>
    <h2 class="ww-h2">Article workspace</h2>${blocked}</div></section>`;

  const record = conn.record as WpRecord;
  const article = primaryArticle(record);
  if (!article) return html`<section class="ww-panel"><div class="ww-card">
    <p class="ww-empty">No article in the connector record.</p></div></section>`;

  const sections = article.sections || [];
  const maxShare = Math.max(...sections.map((s) => s.shareOfBody), 0.0001);
  return html`<section class="ww-panel"><div class="ww-stack">
    <div class="ww-card">
      <div class="ww-card-head">
        <div><span class="ww-eyebrow">Live article · read-only</span>
          <h2 class="ww-h2">${article.title}</h2></div>
        <span class="ww-source">rev ${article.revisionId ?? '—'} · Action API parse</span>
      </div>
      <div class="ww-annobar">
        <b>Unannotated.</b> The quote-anchored annotation workspace (review comments → drafted COI requests → tasks)
        needs the annotation backend, which is not wired. Below is the article's <b>real</b> lede and section balance,
        read-only — no annotations, article text, or recommendations are fabricated.
      </div>
      ${article.lede
        ? html`<div class="ww-lede-real"><span class="ww-lede-label">LEDE · rev ${article.revisionId ?? '—'}</span>
            <p>${article.lede.text}</p></div>`
        : html`<p class="ww-empty">The lede could not be extracted from the parsed article this fetch.</p>`}
    </div>
    ${sections.length
      ? html`<div class="ww-card">
          <div class="ww-card-head">
            <div><span class="ww-eyebrow">Section map · real prose share</span>
              <h2 class="ww-h2">Article balance</h2></div>
            <span class="ww-source">parsed section byte share · advisory</span>
          </div>
          ${sections.map((s) => html`<div class="ww-sectionrow">
            <b class="ww-sectitle">${s.title || '(lede)'}</b>
            <div class="ww-weightbar"><i style=${`width:${((s.shareOfBody / maxShare) * 100).toFixed(1)}%`}></i></div>
            <code class="ww-mono">${(s.shareOfBody * 100).toFixed(1)}%</code>
          </div>`)}
          <p class="ww-note hub-muted">Undue-weight verdicts are a human judgment — this is the measured share of
            article body per section, not a recommendation.</p>
        </div>`
      : nothing}
  </div></section>`;
}
